import { ActionDto, AiRan5GStatus, AiRanTelemetry, BuildDetailsDto, Cn5gStatus, FlexRicState, FlexRicTelemetryDto, GnbStatus, MachineDto, ModalService, OaiboxStatus, OaiboxTelemetryDto, OaiboxType, Tenant, WebAppCommonModule } from '@allbesmart/web-app-common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from 'rxjs';
import { DeleteMachineWarningModalComponent } from "../delete-machine-warning-modal/delete-machine-warning-modal.component";
import { environment } from "../../../environments/environment";
import { UserRole, WebAppKeycloakAuthModule } from '@allbesmart/web-app-keycloak-auth';
import { BuildService } from 'src/app/services/build.service';
import { UnlinkMachineWarningModalComponent } from '../unlink-machine-warning-modal/unlink-machine-warning-modal.component';
import { v4 } from "uuid";
import { UpdateMachineConfigurationModalComponent } from "../update-machine-configuration-modal/update-machine-configuration-modal.component";
import { OaiboxTelemetryService } from "../../services/oaibox-telemetry.service";
import { FlexRicTelemetryService } from "../../services/flex-ric-telemetry.service";
import { AuthActionConfig, AVAILABLE_ACTIONS } from "../../configs/actions.configs";
import { ActionCommand } from "../../enums/action-command";
import { ActionsService } from "../../services/actions.service";
import Keycloak from "keycloak-js";
import { AvailableBuildCommitDto } from "../../models/configuration-file-details/available-build-commit-dto";
import { AiRanTelemetryService } from 'src/app/services/ai-ran-telemetry.service';
import { isAiRanType } from "../../helpers/oaibox-type";
import { MenuComponent } from '../../shared/components/menu/menu.component';
import { BuildDetailsComponent } from '../build-details/build-details.component';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'oaibox-oaibox-telemetry',
    templateUrl: './oaibox-telemetry.component.html',
    styleUrls: ['./oaibox-telemetry.component.css'],
    imports: [
        WebAppCommonModule,
        MenuComponent,
        WebAppKeycloakAuthModule,
        BuildDetailsComponent,
        DatePipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OaiboxTelemetryComponent implements OnInit, OnDestroy {

  @Input({ required: true }) public tenant!: Tenant;
  @Input({ required: true }) public machine!: MachineDto;

  protected readonly isRoot: boolean;
  protected readonly UserRole = UserRole;

  protected readonly FlexRicState = FlexRicState;
  protected readonly OaiboxStatus = OaiboxStatus;
  protected readonly Cn5gStatus = Cn5gStatus;
  protected readonly GnbStatus = GnbStatus;
  protected readonly AiRan5GStatus = AiRan5GStatus;
  protected readonly OaiboxType = OaiboxType;
  protected readonly dashboardBasePath = environment.dashboardBasePath;

  protected buildDropdownId = v4();
  protected buildDropdownToggleId = v4();

  protected oaiboxTelemetry?: OaiboxTelemetryDto;
  protected aiRanTelemetry?: AiRanTelemetry;
  private oaiboxTelemetrySubscription?: Subscription;

  protected flexRicTelemetry?: FlexRicTelemetryDto;
  private flexRicTelemetrySubscription?: Subscription;

  public build?: BuildDetailsDto;
  private buildDetailsSubscription?: Subscription;

  public availableBuildCommitDto?: AvailableBuildCommitDto;
  private availableBuildCommitSubscription?: Subscription;

  private buildExecutableSubscription?: Subscription;

  protected readonly upgradeOaiboxAction?: AuthActionConfig = AVAILABLE_ACTIONS.OAIBOX.find(action => action.command == ActionCommand.UPGRADE_OAIBOX);
  protected readonly upgradeCN5GAction?: AuthActionConfig = AVAILABLE_ACTIONS.CN5G.find(action => action.command == ActionCommand.UPGRADE_CN5G);
  protected readonly upgradeGNBAction?: AuthActionConfig = AVAILABLE_ACTIONS.gNB.find(action => action.command == ActionCommand.UPGRADE_GNB);
  protected readonly upgradeFlexRicAction?: AuthActionConfig = AVAILABLE_ACTIONS.FLEXRIC.find(action => action.command == ActionCommand.UPGRADE_FLEXRIC);
  protected readonly upgradeUhdAction?: AuthActionConfig = AVAILABLE_ACTIONS.USRP.find(action => action.command == ActionCommand.UPGRADE_UHD);

  constructor(
    private keycloak: Keycloak,
    private oaiboxTelemetryService: OaiboxTelemetryService,
    private aiRanTelemetryService: AiRanTelemetryService,
    private flexRicTelemetryService: FlexRicTelemetryService,
    private modalService: ModalService,
    private buildService: BuildService,
    private actionsService: ActionsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.isRoot = keycloak.hasRealmRole(UserRole.ROOT);
  }

  ngOnInit(): void {
    if (this.keycloak.hasRealmRole(UserRole.ROOT)) {
      this.buildDetailsSubscription = this.buildService.getBuildDetails$().subscribe(buildDetails => {
        this.build = buildDetails.find(build => build.tenantId === this.tenant.id && build.machineId === this.machine.id);
        this.changeDetectorRef.markForCheck();
      });
    } else if (this.keycloak.hasRealmRole(UserRole.RESELLER)) {
      this.availableBuildCommitSubscription = this.buildService.getAvailableBuildCommit$()
        .subscribe(availableBuildCommitDto => {
          this.availableBuildCommitDto = availableBuildCommitDto;
          this.changeDetectorRef.markForCheck();
        });
    }

    if (isAiRanType(this.machine.productDetails.oaiboxType)) {
      this.oaiboxTelemetrySubscription = this.aiRanTelemetryService
        .getAiRanTelemetry$(this.machine.id)
        .subscribe(telemetry => {
          this.aiRanTelemetry = telemetry;
          this.changeDetectorRef.markForCheck();
        });
    } else {
      this.oaiboxTelemetrySubscription = this.oaiboxTelemetryService
        .getOaiboxTelemetry$(this.machine.id)
        .subscribe(telemetry => {
          this.oaiboxTelemetry = telemetry;
          this.changeDetectorRef.markForCheck();
        });
    }

    this.flexRicTelemetrySubscription = this.flexRicTelemetryService
      .getFlexRicTelemetry$(this.machine.id)
      .subscribe(flexRicTelemetry => {
        this.flexRicTelemetry = flexRicTelemetry;
        this.changeDetectorRef.markForCheck();
      });
  }

  protected unlinkMachine(): void {
    this.modalService.pushModal({
      component: UnlinkMachineWarningModalComponent,
      inputs: new Map<string, any>([
        ['tenant', this.tenant],
        ['machine', this.machine],
      ]),
    })
  }

  protected updateMachine(): void {
    this.modalService.pushModal({
      component: UpdateMachineConfigurationModalComponent,
      inputs: new Map<string, any>([
        ['tenant', this.tenant],
        ['machine', this.machine],
      ]),
      size: 'medium',
    })
  }

  protected deleteMachine(): void {
    this.modalService.pushModal({
      component: DeleteMachineWarningModalComponent,
      inputs: new Map<string, any>([
        ['tenant', this.tenant],
        ['machine', this.machine],
      ]),
    })
  }

  public downloadBuildExecutable(): void {
    const platform = this.tenant.registeredMachines
      .find(m => m.id === this.machine.id && m.productDetails.platform)
      ?.productDetails.platform;

    this.buildExecutableSubscription?.unsubscribe();
    this.buildExecutableSubscription = this.buildService.fetchBuildExecutable(this.tenant.id, this.machine.id, platform)
      .subscribe(response => {
        const blob = response.body;
        const filename = 'oaibox.runner';

        if (blob) {
          const fileURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = filename;
          link.click();

          window.URL.revokeObjectURL(fileURL);
        }
      });
  }

  protected sendAction: (action: ActionDto) => void = (action: ActionDto): void => {
    this.actionsService.publishAction(this.tenant.id, this.machine.id, action);
  };

  ngOnDestroy(): void {
    this.buildDetailsSubscription?.unsubscribe();
    this.availableBuildCommitSubscription?.unsubscribe();
    this.oaiboxTelemetrySubscription?.unsubscribe();
    this.flexRicTelemetrySubscription?.unsubscribe();
    this.buildExecutableSubscription?.unsubscribe();
  }

}
